import React, { useEffect, useState, useCallback } from 'react';
import { useSDK } from '../context/SDKContext.tsx';
import type { PendingDossierItem } from '@assistia/sdk';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Filter,
  Eye,
  X,
  AlertTriangle,
  FileText,
  UserCheck,
} from 'lucide-react';

export default function HitlPage(): React.ReactElement {
  const { sdk, isSimulated } = useSDK();
  const [dossiers, setDossiers] = useState<PendingDossierItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [classificationFilter, setClassificationFilter] = useState<string>('');
  const [docTypeFilter, setDocTypeFilter] = useState<string>('');

  // Selected dossier for validation drawer
  const [selectedDossier, setSelectedDossier] = useState<PendingDossierItem | null>(null);
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sdk.suspensions.getPending(
        classificationFilter || undefined,
        docTypeFilter || undefined
      );
      setDossiers(response.data.dossiers);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sdk, classificationFilter, docTypeFilter]);

  useEffect(() => {
    void loadPending();
  }, [loadPending]);

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDossier || !decision) return;
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const isApproved = decision === 'approve';
      await sdk.suspensions.validate(selectedDossier.dossier_id, isApproved, comment);
      setActionSuccess(`Le dossier a été ${isApproved ? 'approuvé' : 'rejeté'} avec succès.`);
      // Refresh list
      void loadPending();
      // Close drawer after short delay
      setTimeout(() => {
        setSelectedDossier(null);
        setDecision(null);
        setComment('');
        setActionSuccess(null);
      }, 1500);
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const getClassificationColor = (cls?: string) => {
    if (!cls) return 'bg-muted text-muted-foreground';
    const mapping: Record<string, string> = {
      C1: 'bg-green-500/10 text-green-700 border-green-500/20',
      C2: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
      C3: 'bg-orange-500/10 text-orange-700 border-orange-500/20',
      C4: 'bg-destructive/10 text-destructive border-destructive/20',
    };
    return mapping[cls] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">File d&apos;attente HITL</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Validation humaine (Human-in-the-Loop) pour les dossiers suspendus ou nécessitant une revue
            {isSimulated && <span className="ml-2 text-orange-500 font-semibold">(simulation)</span>}
          </p>
        </div>
        <button
          onClick={() => { void loadPending(); }}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-muted disabled:opacity-50 transition-colors bg-card text-foreground"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive border border-destructive/20">
          Erreur de chargement : {error}
        </div>
      )}

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl border border-border bg-card shadow-sm">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mr-2">
          <Filter size={16} />
          <span>Filtres :</span>
        </div>

        {/* Classification Filter */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-muted-foreground">Classification</label>
          <select
            value={classificationFilter}
            onChange={(e) => setClassificationFilter(e.target.value)}
            className="px-3 py-1.5 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          >
            <option value="">Toutes</option>
            <option value="C1">C1 (Public)</option>
            <option value="C2">C2 (Interne)</option>
            <option value="C3">C3 (Sensible)</option>
            <option value="C4">C4 (Critique)</option>
          </select>
        </div>

        {/* Doc Type Filter */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-muted-foreground font-medium">Type Doc</label>
          <select
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.target.value)}
            className="px-3 py-1.5 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          >
            <option value="">Tous</option>
            <option value="CLAUSE_BENEF">Clause Bénéficiaire</option>
            <option value="BORDEREAU">Bordereau</option>
          </select>
        </div>
      </div>

      {/* List / Table */}
      {loading && dossiers.length === 0 ? (
        <div className="text-sm text-muted-foreground">Chargement des dossiers en attente…</div>
      ) : dossiers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center bg-card">
          <CheckCircle2 size={36} className="mx-auto text-green-500 mb-3" />
          <h3 className="font-semibold text-foreground text-base">Aucun dossier en attente</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Tous les dossiers ont été traités ou validés.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-muted/50 border-b border-border text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 font-medium">ID Dossier</th>
                <th className="px-6 py-4 font-medium">Type Doc</th>
                <th className="px-6 py-4 font-medium">Classification</th>
                <th className="px-6 py-4 font-medium">Confiance</th>
                <th className="px-6 py-4 font-medium">Motif HITL</th>
                <th className="px-6 py-4 font-medium">Date création</th>
                <th className="px-6 py-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {dossiers.map((d) => (
                <tr
                  key={d.dossier_id}
                  className="hover:bg-muted/20 transition-colors"
                >
                  <td className="px-6 py-4 font-mono text-xs text-foreground select-all">
                    {d.dossier_id}
                  </td>
                  <td className="px-6 py-4 text-foreground font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <FileText size={14} className="text-muted-foreground" />
                      {d.doc_type || 'Inconnu'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${getClassificationColor(d.classification)}`}>
                      {d.classification || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-foreground">
                    {d.confidence !== undefined ? (
                      <span className={`font-semibold ${d.confidence < 0.8 ? 'text-orange-500' : 'text-green-600'}`}>
                        {(d.confidence * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-muted-foreground max-w-xs truncate" title={d.hitl_reason}>
                    {d.hitl_reason || '—'}
                  </td>
                  <td className="px-6 py-4 text-muted-foreground text-xs">
                    {d.created_at ? new Date(d.created_at).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => {
                        setSelectedDossier(d);
                        setDecision('approve');
                        setComment('');
                        setActionError(null);
                        setActionSuccess(null);
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <Eye size={12} />
                      Revoir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Decision Drawer/Overlay */}
      {selectedDossier && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-xs transition-opacity duration-300">
          <div className="w-full max-w-lg bg-card h-full border-l border-border shadow-2xl flex flex-col p-6 space-y-6 relative overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h2 className="text-lg font-bold text-foreground">Revue du Dossier</h2>
                <p className="text-xs text-muted-foreground font-mono mt-1 select-all">{selectedDossier.dossier_id}</p>
              </div>
              <button
                onClick={() => setSelectedDossier(null)}
                className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Info Summary */}
            <div className="rounded-xl bg-muted/30 p-4 border border-border/50 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="block text-xs font-semibold text-muted-foreground uppercase">Type Document</span>
                  <span className="text-foreground font-semibold mt-0.5 block">{selectedDossier.doc_type || '—'}</span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-muted-foreground uppercase">Classification</span>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border mt-1 ${getClassificationColor(selectedDossier.classification)}`}>
                    {selectedDossier.classification || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-muted-foreground uppercase">Confiance Modèle</span>
                  <span className="text-foreground font-semibold mt-0.5 block">
                    {selectedDossier.confidence !== undefined ? `${(selectedDossier.confidence * 100).toFixed(0)}%` : '—'}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-muted-foreground uppercase">Date Soumission</span>
                  <span className="text-foreground mt-0.5 block text-xs">
                    {selectedDossier.created_at ? new Date(selectedDossier.created_at).toLocaleString('fr-FR') : '—'}
                  </span>
                </div>
              </div>

              {selectedDossier.hitl_reason && (
                <div className="pt-2 border-t border-border/50 flex items-start gap-2 text-xs text-orange-600 bg-orange-500/5 p-2 rounded-md border border-orange-500/10">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold block">Motif de suspension (HITL) :</span>
                    <span className="mt-0.5 block">{selectedDossier.hitl_reason}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleValidate} className="flex-1 flex flex-col justify-between">
              <div className="space-y-6">
                {/* Decision */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground block">Décision de validation</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setDecision('approve')}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl border text-sm transition-all ${
                        decision === 'approve'
                          ? 'border-green-500 bg-green-500/5 text-green-700 font-semibold ring-1 ring-green-500'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                      }`}
                    >
                      <UserCheck size={20} className="mb-2" />
                      <span>Approuver</span>
                      <span className="text-[10px] opacity-80 mt-1 font-normal font-sans">Valider la classification &amp; extractions</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDecision('reject')}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl border text-sm transition-all ${
                        decision === 'reject'
                          ? 'border-destructive bg-destructive/5 text-destructive font-semibold ring-1 ring-destructive'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                      }`}
                    >
                      <XCircle size={20} className="mb-2" />
                      <span>Rejeter</span>
                      <span className="text-[10px] opacity-80 mt-1 font-normal font-sans">Suspendre ou annuler le dossier</span>
                    </button>
                  </div>
                </div>

                {/* Comment */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground block">Commentaire / Justification (optionnel)</label>
                  <textarea
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary placeholder:text-muted-foreground"
                    placeholder="Saisissez un commentaire justifiant votre décision..."
                  />
                </div>

                {/* Notifications inside Drawer */}
                {actionError && (
                  <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive border border-destructive/20 font-medium">
                    {actionError}
                  </div>
                )}

                {actionSuccess && (
                  <div className="rounded-md bg-green-500/10 p-3 text-xs text-green-700 border border-green-500/20 font-medium flex items-center gap-1.5">
                    <CheckCircle2 size={14} />
                    {actionSuccess}
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center gap-3 pt-6 border-t border-border mt-6 bg-card sticky bottom-0">
                <button
                  type="button"
                  onClick={() => setSelectedDossier(null)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || !decision}
                  className={`flex-grow-[2] inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm rounded-lg transition-colors font-semibold ${
                    decision === 'reject'
                      ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  } disabled:opacity-50`}
                >
                  {actionLoading ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Traitement…
                    </>
                  ) : (
                    'Confirmer la décision'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
